// SCADA Entegrasyonu — API istemcisi.
//
// Sunucu sözleşmesi: services/api/Btys.Api/Modules/Entegrasyon/Dto.cs
// Ajan sözleşmesi:   docs/ENTEGRASYON.md (ProView deposu) — paket varyantları,
//                    kalite değerleri ve yaş göstergesinin anlamı oradan.
//
// Enum'lar sunucudan METİN iner (JsonStringEnumConverter, Program.cs:86); alan
// adları camelCase Türkçe (§7.0).

import { api } from '../../lib/api'

export type EntegrasyonDurumu = 'aktif' | 'pasif'

/** Sözleşme §3.2–3.5'teki dört paket varyantı. */
export type PaketTuru = 'okuma' | 'bilinmeyen' | 'goruntu_yok' | 'hata'

/** Sözleşme §3.2: `quality` taşıyan OCR değeri · `distance` taşıyan renk-durum. */
export type EtiketTuru = 'sayisal' | 'durum'

export type KaynakSatiri = {
  id: string
  kod: string
  ad: string
  aciklama: string | null
  durum: EntegrasyonDurumu
  ajanSurumu: string | null
  sonPaketZamani: string | null
  /** Sunucu saatine göre yaş (sn) — istemci kendi saatinden hesaplamaz. */
  sonPaketYasiSn: number | null
  sonSeq: number | null
  paketSayisi: number
  sayfaSayisi: number
  etiketSayisi: number
  anahtarSayisi: number
  ucNoktaUrl: string
}

/**
 * Paketin `host` nesnesi (§3.1) — ajanın ÖZ sağlığı, okuma verisi değil.
 * Anahtarlar ajanın yazdığı gibi (snake_case) duruyor: sunucu bu nesneyi
 * çözmeden geçiriyor, biz de yeniden adlandırmıyoruz — ajan yeni bir alan
 * eklerse burada adı değişmeden görünsün (§3.6 ileri uyumluluk).
 */
export type AjanSagligi = {
  cpu_pct?: number | null
  ram_mb?: number | null
  gdi?: number | null
  resource_warning?: boolean | null
}

export type SayfaSatiri = {
  id: string
  kaynakId: string
  sayfaKodu: string
  /** Boşsa etiket kaynak genelinde tek; doluysa sayfa koduyla ayrılmış. */
  ayrim: string
  ad: string
  aciklama: string | null
  durum: EntegrasyonDurumu
  /** Kullanıcı tanımlamadan ilk paketle açıldıysa true. */
  otomatikMi: boolean
  sonGorulme: string | null
  sonGorulmeYasiSn: number | null
  paketSayisi: number
  etiketSayisi: number
  ucNoktaUrl: string
}

export type AnahtarSatiri = {
  id: string
  kaynakId: string
  sayfaId: string | null
  sayfaKodu: string | null
  ad: string
  /** İlk 10 karakter — listede tanımak için; gizli değil. */
  onek: string
  durum: EntegrasyonDurumu
  olusturmaZamani: string
  sonKullanim: string | null
  iptalZamani: string | null
  ucNoktaUrl: string
  /**
   * Düz metin geri gösterilebiliyor mu? Kasa kolonundan ÖNCE üretilmiş
   * anahtarlarda yalnız hash var; ekran bunu "eski anahtar" diye söylüyor.
   */
  gosterilebilir: boolean
  /**
   * Anahtar bir sayfa için üretildiyse ve o sayfa SONRADAN SİLİNDİYSE, silinen
   * sayfanın kodu. Ekranda gösterilmesi zorunlu — gösterilmezse satır, doğuştan
   * kaynak kapsamlı üretilmiş bir anahtardan ayırt edilemez.
   */
  oncekiSayfaKodu: string | null
}

/** Bir durum bandı — grafikte tek çubuk. `durum` aynı zamanda satır adı. */
export type DurumBandi = {
  bas: string
  son: string
  durum: string
  adet: number
  /** Kova modunda bandın o kovadaki payı (0-1); aralık modunda 1. */
  oran: number
}

export type DurumKarnesi = { durum: string; bantSayisi: number; toplamSn: number }

/** Sayısal olmayan etiketin durum zaman çizelgesi. */
export type DurumCizelgesi = {
  etiket: EtiketSatiri
  /** Kullanıcının SEÇTİĞİ pencere — grafik ekseni buna sabitlenir. */
  bas: string
  son: string
  /** 'aralik' gerçek geçişler · 'kova' taşma modu (kovanın baskın durumu). */
  mod: string
  kovaSn: number | null
  boslukEsigiSn: number
  /** Kovalamadan önceki gerçek bant sayısı. */
  hamBantSayisi: number
  bantlar: DurumBandi[]
  karne: DurumKarnesi[]
}

export type ToplamaYontemi = 'integral' | 'fark' | 'ortalama'

export type HizBirimi = { birim: string; bolen: number; toplamBirimi: string }

export type ToplamaTanimi = {
  etiketId: string
  yontem: ToplamaYontemi
  okumaBirimi: string | null
  toplamBirimi: string | null
  birimBoleni: number | null
  maksBoslukSn: number
  durum: string
}

export type SaatToplami = {
  baslangic: string
  toplam: number
  birim: string | null
  ornekAdedi: number
  kapsananSn: number
  boslukSn: number
  /** 0-1 arası güven ölçüsü: dönemin ne kadarı gerçekten veriyle kaplandı. */
  kapsam: number
}

export type SaatlikToplamlar = {
  bas: string
  son: string
  toplam: number
  ortalamaKapsam: number
  saatler: SaatToplami[]
}

export type AralikToplami = {
  bas: string
  son: string
  yontem: ToplamaYontemi
  toplam: number
  hamToplam: number
  birim: string | null
  ornekAdedi: number
  kapsananSn: number
  boslukSn: number
  enUzunBoslukSn: number
  kapsam: number
}

export type AnahtarUretimi = { anahtar: AnahtarSatiri; duzMetin: string }

/** Kaydedilmiş anahtarın düz metni. `duzMetin` boşsa `not` nedenini söylüyor. */
export type AnahtarGoster = {
  id: string
  onek: string
  duzMetin: string | null
  not: string | null
}

export type EtiketSatiri = {
  id: string
  sayfaId: string | null
  anahtar: string
  ad: string | null
  birim: string | null
  tur: EtiketTuru
  /** Ekrandan okunan HAM metin (TR ondalık virgül) — §3.2. */
  sonHam: string | null
  /** Ayrıştırılmış sayı; hesap için tek otoriter alan. Renk-durumda null. */
  sonSayi: number | null
  sonKalite: string | null
  sonZaman: string | null
  sonZamanYasiSn: number | null
  olcumSayisi: number
  uniteId: string | null
  beyanParametresiId: string | null
  carpan: number | null
  ofset: number | null
}

export type OlcumNoktasi = {
  zaman: string
  sayi: number | null
  ham: string
  kalite: string
  yanip: boolean | null
  kararli: boolean | null
}

export type Seri = {
  etiket: EtiketSatiri
  bas: string | null
  son: string | null
  /** Zamana göre ARTAN — sunucu böyle döndürüyor. */
  noktalar: OlcumNoktasi[]
}

export type PaketSatiri = {
  id: string
  kaynakId: string
  kaynakKod: string
  sayfaId: string | null
  sayfaKodu: string | null
  seq: number
  tur: PaketTuru
  yakalamaZamani: string | null
  gonderimZamani: string | null
  alinmaZamani: string
  /** sent_at − captured_at: paket diskte bekledi mi (kesinti göstergesi, §3.1). */
  kuyruklamaSn: number | null
  kareImzasi: string | null
  hata: string | null
  okumaSayisi: number
}

export type KaynakDetay = {
  kaynak: KaynakSatiri
  sonSaglik: AjanSagligi | null
  sayfalar: SayfaSatiri[]
  anahtarlar: AnahtarSatiri[]
}

export type SayfaDetay = { sayfa: SayfaSatiri; etiketler: EtiketSatiri[] }

export type KaynakIstek = { kod: string; ad: string; aciklama: string | null }
export type KaynakGuncelleIstek = {
  ad: string
  aciklama: string | null
  durum: EntegrasyonDurumu
}
export type SayfaIstek = { sayfaKodu: string; ad: string; aciklama: string | null }
export type SayfaGuncelleIstek = {
  ad: string
  aciklama: string | null
  durum: EntegrasyonDurumu
}
export type AnahtarIstek = { ad: string }
export type EtiketGuncelleIstek = {
  ad: string | null
  birim: string | null
  uniteId: string | null
  beyanParametresiId: string | null
  /** 0 gönderilemez — sunucu 409 döner (dönüşüm her okumayı sıfırlar). */
  carpan: number | null
  ofset: number | null
}


// ------------------------------------------------------ tüm etiketler görünümü

/** "Tüm etiketler" tablosunun satırı — etiket + kaynağı/sayfası + aralık özeti. */
export type EtiketOzetSatiri = {
  id: string
  kaynakId: string
  kaynakKod: string
  kaynakAd: string
  sayfaId: string | null
  sayfaKodu: string
  sayfaAd: string
  anahtar: string
  ad: string | null
  birim: string | null
  tur: EtiketTuru
  uniteId: string | null
  uniteKod: string | null
  beyanParametresiId: string | null
  sonHam: string | null
  sonSayi: number | null
  sonKalite: string | null
  sonZaman: string | null
  sonZamanYasiSn: number | null
  /** Seçilen aralıktaki ölçüm sayısı; 0 ise etiket o aralıkta hiç akmamış. */
  adet: number
  min: number | null
  maks: number | null
  ortalama: number | null
}

export type Sayim = { deger: string; adet: number }

export type Istatistik = {
  adet: number
  /** Sayısı olan ölçüm adedi; adet'ten küçükse okunamayan ölçümler var. */
  sayisalAdet: number
  min: number | null
  maks: number | null
  ortalama: number | null
  medyan: number | null
  stdSapma: number | null
  ilk: number | null
  son: number | null
  /** Son − İlk. Sayaç tipi etikette aralıktaki tüketim demektir. */
  fark: number | null
  /** Maks − Min. */
  yayilim: number | null
  ilkZaman: string | null
  sonZaman: string | null
  /** Ortalama örnekleme aralığı (sn) — ajan periyodunun ölçüsü. */
  ortOrneklemeSn: number | null
  kalite: Sayim[]
  durum: Sayim[]
}

/** Grafiğin tek noktası: kova başlangıcı + o kovanın özeti. */
export type Kova = {
  zaman: string
  adet: number
  min: number | null
  maks: number | null
  ortalama: number | null
}

export type EtiketAnalizi = {
  etiket: EtiketSatiri
  kaynakKod: string
  kaynakAd: string
  sayfaKodu: string
  bas: string
  son: string
  /** Kova genişliği (sn) — ekran "5 dk'lık ortalama" diye yazıyor. */
  kovaSn: number
  kovalar: Kova[]
  istatistik: Istatistik
}

export const entegrasyon = {
  kaynaklar: () => api<KaynakSatiri[]>('/api/entegrasyon/kaynaklar'),

  kaynak: (id: string) => api<KaynakDetay>(`/api/entegrasyon/kaynaklar/${id}`),

  kaynakOlustur: (govde: KaynakIstek) =>
    api<KaynakSatiri>('/api/entegrasyon/kaynaklar', { yontem: 'POST', govde }),

  kaynakGuncelle: (id: string, govde: KaynakGuncelleIstek) =>
    api<KaynakSatiri>(`/api/entegrasyon/kaynaklar/${id}`, { yontem: 'PUT', govde }),

  sayfaOlustur: (kaynakId: string, govde: SayfaIstek) =>
    api<SayfaSatiri>(`/api/entegrasyon/kaynaklar/${kaynakId}/sayfalar`, {
      yontem: 'POST',
      govde,
    }),

  sayfa: (id: string) => api<SayfaDetay>(`/api/entegrasyon/sayfalar/${id}`),

  /** Silinirse ne kaybedilecek — çift onaylı silmenin ilk adımı. */
  sayfaSilmeOzeti: (id: string) =>
    api<{
      id: string
      sayfaKodu: string
      ad: string
      etiket: number
      olcum: number
      paket: number
      sonVeri: string | null
      /** Ajan hâlâ gönderiyorsa sayfa ilk pakette yeniden açılır. */
      otomatikDoner: boolean
      /** Bir üniteye/beyan parametresine bağlanmış etiket sayısı. */
      bagliEtiket: number
  /** Başka ekranlarda da okunan, bu yüzden SİLİNMEYECEK etiket sayısı. */
  kalacakEtiket: number
      /** Bu sayfa için üretilmiş anahtarlar — SİLİNMEZ, yalnız bağı kopar. */
      anahtarlar: { id: string; ad: string; onek: string; durum: string }[]
    }>(`/api/entegrasyon/sayfalar/${id}/silme-ozeti`),

  /** Sayfayı, etiketlerini ve ölçümlerini siler. Ham paketler korunur. */
  sayfaSil: (id: string) =>
    api<{
      silindi: boolean
      sayfaKodu: string
      etiket: number
      olcum: number
      paketBagiKopan: number
      anahtarBagiKopan: number
    }>(`/api/entegrasyon/sayfalar/${id}`, { yontem: 'DELETE' }),

  sayfaGuncelle: (id: string, govde: SayfaGuncelleIstek) =>
    api<SayfaSatiri>(`/api/entegrasyon/sayfalar/${id}`, { yontem: 'PUT', govde }),

  kaynakAnahtari: (kaynakId: string, govde: AnahtarIstek) =>
    api<AnahtarUretimi>(`/api/entegrasyon/kaynaklar/${kaynakId}/anahtarlar`, {
      yontem: 'POST',
      govde,
    }),

  sayfaAnahtari: (sayfaId: string, govde: AnahtarIstek) =>
    api<AnahtarUretimi>(`/api/entegrasyon/sayfalar/${sayfaId}/anahtarlar`, {
      yontem: 'POST',
      govde,
    }),

  /** İptal SİLMEZ: durum pasife düşer, kayıt denetim izinde kalır. */
  anahtarIptal: (id: string) =>
    api<AnahtarSatiri>(`/api/entegrasyon/anahtarlar/${id}`, { yontem: 'DELETE' }),

  /** Hız birimi sözlüğü — kapalı küme, seçici bunu kullanıyor. */
  hizBirimleri: () => api<HizBirimi[]>('/api/entegrasyon/birimler/hiz'),

  toplamaTanimi: (id: string) =>
    api<ToplamaTanimi | null>(`/api/entegrasyon/etiketler/${id}/toplama`),

  toplamaYaz: (id: string, g: {
    yontem: ToplamaYontemi
    okumaBirimi?: string | null
    maksBoslukSn?: number
  }) =>
    api<ToplamaTanimi>(`/api/entegrasyon/etiketler/${id}/toplama`, {
      yontem: 'PUT',
      govde: g,
    }),

  toplamaKapat: (id: string) =>
    api<void>(`/api/entegrasyon/etiketler/${id}/toplama`, { yontem: 'DELETE' }),

  /** Kaydedilmiş SAATLİK toplamlar — arka plan işinin yazdıkları. */
  saatlikToplamlar: (id: string, p: { bas: string; son: string }) =>
    api<SaatlikToplamlar>(
      `/api/entegrasyon/etiketler/${id}/toplamlar?bas=${encodeURIComponent(p.bas)}&son=${encodeURIComponent(p.son)}`,
    ),

  /**
   * ÖZEL ARALIK toplamı — ham ölçümden yeniden hesaplanıyor.
   * `yontem`/`birim` verilirse tanım kaydedilmeden ÖNİZLEME yapılıyor.
   */
  aralikToplami: (id: string, p: {
    bas: string
    son: string
    yontem?: ToplamaYontemi
    birim?: string
  }) => {
    const s = new URLSearchParams({ bas: p.bas, son: p.son })
    if (p.yontem) s.set('yontem', p.yontem)
    if (p.birim) s.set('birim', p.birim)
    return api<AralikToplami>(`/api/entegrasyon/etiketler/${id}/toplam?${s}`)
  },

  /**
   * Sayısal olmayan etiketin durum zaman çizelgesi — her durum bir satır,
   * sürdüğü aralıklar bant. Ayrı uç: analiz her 30 sn tazeleniyor, çizelge
   * sorgusu ondan pahalı ve yalnız durum etiketinde çağrılıyor.
   */
  cizelge: (id: string, p: { bas?: string; son?: string } = {}) => {
    const s = new URLSearchParams()
    if (p.bas) s.set('bas', p.bas)
    if (p.son) s.set('son', p.son)
    const sorgu = s.toString()
    return api<DurumCizelgesi>(
      `/api/entegrasyon/etiketler/${id}/cizelge${sorgu ? `?${sorgu}` : ''}`,
    )
  },

  /**
   * Tüm kaynakların tüm etiketleri + seçilen aralığın özeti.
   * Aralık verilmezse sunucu son 24 saati alıyor.
   */
  tumEtiketler: (p: { bas?: string; son?: string; q?: string } = {}) => {
    const s = new URLSearchParams()
    if (p.bas) s.set('bas', p.bas)
    if (p.son) s.set('son', p.son)
    if (p.q) s.set('q', p.q)
    const sorgu = s.toString()
    return api<EtiketOzetSatiri[]>(`/api/entegrasyon/etiketler${sorgu ? `?${sorgu}` : ''}`)
  },

  /**
   * Detay alanı: aralığın kovalanmış grafiği + istatistik.
   *
   * Kovalama SUNUCUDA: seri ucu en yeni N noktayı döndürüyor, 7 günlük bir
   * aralıkta o N nokta yalnız son birkaç saati kapsardı ve grafik seçilen
   * aralığı temsil etmezdi.
   */
  analiz: (id: string, p: { bas?: string; son?: string; kova?: number } = {}) => {
    const s = new URLSearchParams()
    if (p.bas) s.set('bas', p.bas)
    if (p.son) s.set('son', p.son)
    if (p.kova) s.set('kova', String(p.kova))
    const sorgu = s.toString()
    return api<EtiketAnalizi>(
      `/api/entegrasyon/etiketler/${id}/analiz${sorgu ? `?${sorgu}` : ''}`,
    )
  },

  /**
   * Kaydedilmiş anahtarın düz metnini getirir.
   *
   * Yazma yetkisi ister (anahtarı görmek onu ele geçirmekle aynı güçte) ve her
   * çağrı sunucuda denetim satırına yazılır.
   */
  anahtarGoster: (id: string) =>
    api<AnahtarGoster>(`/api/entegrasyon/anahtarlar/${id}/goster`),

  /**
   * İptal edilmiş anahtarı kaydıyla birlikte siler. Etkin anahtarda 409 —
   * silinen anahtarla gelen paket 401 alır ve ajan onu bir daha göndermez.
   */
  anahtarKaliciSil: (id: string) =>
    api<void>(`/api/entegrasyon/anahtarlar/${id}/kalici`, { yontem: 'DELETE' }),

  etiketGuncelle: (id: string, govde: EtiketGuncelleIstek) =>
    api<EtiketSatiri>(`/api/entegrasyon/etiketler/${id}`, { yontem: 'PUT', govde }),

  seri: (etiketId: string, adet = 200) =>
    api<Seri>(`/api/entegrasyon/etiketler/${etiketId}/seri?adet=${adet}`),

  /**
   * Tek paketin ham gövdesi. Listeden AYRI: gövdeler listeyle birlikte
   * geliyordu ve 500 paketlik bir sorgu her gövdeyi (paket başına onlarca KB)
   * taşıyordu. Ekran gövdeyi zaten yalnız satır açıldığında gösteriyor.
   */
  paketGovdesi: (id: string) =>
    api<{ id: string; govde: unknown }>(`/api/entegrasyon/paketler/${id}/govde`),

  paketler: (kaynakId?: string, adet = 50) =>
    api<PaketSatiri[]>(
      `/api/entegrasyon/paketler?adet=${adet}${kaynakId ? `&kaynakId=${kaynakId}` : ''}`,
    ),
}
